// Gross Operating Profit (GOP) helpers.
//
// GOP is the profit of the operated departments — the sum of each department's
// (revenue − expense) where a revenue department has a MATCHING expense
// department (e.g. ROOMS REVENUE ↔ ROOMS EXPENSES, FOOD REVENUES ↔ FOOD COST).
// It deliberately excludes net income's undistributed / fixed lines (utilities,
// A&G, capital, etc.) that have no matching revenue department.

// Normalizes a department label to its base name so revenue/expense pairs match:
// "ROOMS REVENUE" → "ROOMS", "FOOD REVENUES"/"FOOD COST" → "FOOD".
export function baseDeptKey(label) {
  return String(label || '')
    .toUpperCase()
    .replace(/\b(REVENUES?|EXPENSES?|COSTS?)\b/g, ' ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

/**
 * Computes GOP and GOP margin from a generateFinancialReport pnl structure.
 * GOP margin uses total operating revenue as the denominator.
 *
 * @returns {{ actual:number, budget:number, matched:number,
 *             marginActual:(number|null), marginBudget:(number|null) }}
 */
export function computeGop(reportData, actualKey, budgetKey) {
  const revDepts = reportData?.pnl?.revenue?.departments || [];
  const expDepts = reportData?.pnl?.expense?.departments || [];

  const expByBase = {};
  for (const d of expDepts) {
    const k = baseDeptKey(d.label);
    if (k) (expByBase[k] = expByBase[k] || []).push(d);
  }

  let actual = 0;
  let budget = 0;
  let matched = 0;
  for (const rd of revDepts) {
    const matches = expByBase[baseDeptKey(rd.label)];
    if (!matches || matches.length === 0) continue; // only departments with a matched expense side
    const rA = Number(rd.subtotal?.[actualKey] || 0);
    const rB = budgetKey ? Number(rd.subtotal?.[budgetKey] || 0) : 0;
    let eA = 0;
    let eB = 0;
    for (const ed of matches) {
      eA += Number(ed.subtotal?.[actualKey] || 0);
      eB += budgetKey ? Number(ed.subtotal?.[budgetKey] || 0) : 0;
    }
    actual += rA - eA;
    budget += rB - eB;
    matched += 1;
  }

  const revActual = Number(reportData?.pnl?.revenue?.totals?.[actualKey] || 0);
  const revBudget = budgetKey ? Number(reportData?.pnl?.revenue?.totals?.[budgetKey] || 0) : 0;

  return {
    actual,
    budget,
    matched,
    marginActual: revActual ? (actual / revActual) * 100 : null,
    marginBudget: budgetKey && revBudget ? (budget / revBudget) * 100 : null,
  };
}
